import React from 'react';
import PropTypes from 'prop-types';
import Link from 'next/link';
import { ArrowRight, BadgeCheck, Star } from 'lucide-react';
import { CloudinaryPicture } from 'components/rich';

import { Head, Layout } from '../components/layout';
import styles from '../components/about/about.module.scss';
import tracker from '../lib/tracker';

const IMAGE_PREFIX = 'products/';
const IMAGE_WIDTHS = [320, 480, 640];
const CLOSING_URL = '/ancestry-family-history';

const HERO_FACTS = [
  { title: 'Since 2007', body: 'Printing family history for nearly two decades' },
  { title: 'Made in the USA', body: 'By our teams in South Carolina, Pennsylvania and Utah' },
  { title: 'Built on your tree', body: 'Connects to Ancestry.com and FamilySearch.org' },
];

const TIMELINE = [
  { label: '2007', body: 'MyCanvas launches to turn family trees into keepsakes' },
  { label: 'Today', body: 'Books, posters and calendars, made in the USA' },
];

const STATES = [
  { name: 'South Carolina', code: 'SC' },
  { name: 'Pennsylvania', code: 'PA' },
  { name: 'Utah', code: 'UT' },
];

const PRODUCT_TILES = [
  {
    id: 'family-history-books',
    imageKey: 'ancestry-book/5',
    title: 'Family History Books',
    description: 'Your Ancestry tree as a book, with family group sheets and individual timelines.',
    linkLabel: 'Shop family history',
    url: '/ancestry-family-history',
  },
  {
    id: 'family-tree-posters',
    imageKey: 'ancestry-poster/8',
    title: 'Family Tree Posters',
    description: 'Generations of your family on one poster, made for your wall.',
    linkLabel: 'Shop family tree posters',
    url: '/ancestry-poster',
  },
  {
    id: 'photo-books',
    imageKey: 'premium-hardcover-books_mycanvas/3',
    title: 'Photo Books',
    description:
      'Softcover, Hardcover and Premium Hardcover books for the photos you treasure most.',
    linkLabel: 'Shop photo books',
    url: '/photo-books',
  },
];

const trackCtaClick = (cta, location, target) =>
  tracker.onCustomEvent('about_cta_click', { cta, location, target });

const Stars = ({ size }) => (
  <span className={styles.stars} aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

Stars.propTypes = {
  size: PropTypes.number.isRequired,
};

const AboutPage = () => (
  <>
    <Head
      title="About Our Family History Printing"
      description="MyCanvas turns family photos and Ancestry or FamilySearch trees into printed books, posters and calendars, all made in the USA in South Carolina, Pennsylvania and Utah."
    />

    <Layout>
      <div className={styles.page}>
        <section className={styles.hero}>
          <div className={`${styles.container} ${styles.heroGrid}`}>
            <div className={styles.heroIntro}>
              <p className={styles.eyebrow}>About MyCanvas</p>
              <h1 className={styles.title}>Family history, printed in the USA since 2007</h1>
              <p className={styles.lead}>
                MyCanvas brings people together. We help bond generations by turning family history
                into art, books, and gifts that bring your past into the present.
              </p>
              <p className={styles.statement}>
                <BadgeCheck size={22} aria-hidden="true" />
                MyCanvas is an American company and is not part of Ancestry.com.
              </p>
            </div>

            <div className={styles.heroMedia}>
              <CloudinaryPicture
                prefix={IMAGE_PREFIX}
                imageKey="premium-hardcover-books_mycanvas/1"
                widths={IMAGE_WIDTHS}
                sizes="(min-width: 992px) 540px, calc(100vw - 32px)"
                className={styles.coverImage}
                priority
              />
              <div className={styles.seal} aria-hidden="true">
                <span className={styles.sealSmall}>Est.</span>
                <span className={styles.sealYear}>2007</span>
                <span className={styles.sealStars}>
                  <Stars size={12} />
                </span>
                <span className={styles.sealSmall}>Made in USA</span>
              </div>
            </div>

            <ul className={styles.facts}>
              {HERO_FACTS.map(({ title, body }) => (
                <li key={title} className={styles.fact}>
                  <span className={styles.factTitle}>{title}</span>
                  <span className={styles.factBody}>{body}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className={styles.history} aria-labelledby="about-history">
          <div className={`${styles.container} ${styles.historyRow}`}>
            <div className={styles.historyText}>
              <p className={styles.eyebrow}>Our history</p>
              <p className={styles.yearMark} aria-hidden="true">
                2007
              </p>
              <span className={styles.highlightRule} aria-hidden="true" />
              <h2 id="about-history" className={styles.sectionTitle}>
                Nearly two decades of family keepsakes
              </h2>
              <p className={styles.historyBody}>
                MyCanvas launched in 2007 to turn family history research into something you can
                hold. Our website connects directly to your family tree on Ancestry.com or
                FamilySearch.org, so your research becomes ready-to-print products.
              </p>
              <ol className={styles.timeline}>
                {TIMELINE.map(({ label, body }) => (
                  <li key={label} className={styles.timelineStep}>
                    <span className={styles.timelineDot} aria-hidden="true" />
                    <span className={styles.timelineLabel}>
                      <span className={styles.timelineYear}>{label}</span>
                      <span className={styles.timelineBody}>{body}</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>

            <div className={styles.historyMedia}>
              <CloudinaryPicture
                prefix={IMAGE_PREFIX}
                imageKey="ancestry-poster/9"
                widths={IMAGE_WIDTHS}
                sizes="(min-width: 992px) 500px, calc(100vw - 32px)"
                className={styles.coverImage}
                loading="lazy"
              />
            </div>
          </div>
        </section>

        <section className={styles.story} aria-labelledby="about-story">
          <div className={`${styles.container} ${styles.storyGrid}`}>
            <p className={styles.eyebrow}>Our story</p>
            <h2 id="about-story" className={styles.sectionTitle}>
              Made to last through generations
            </h2>
            <p className={styles.storyParagraph}>
              Family stories help strengthen our connections with each other. We collectively gain
              courage from stories of ancestors who bravely faced hardship. We bond over a shared
              smile while admiring grandma’s old hair-dos.
            </p>
            <blockquote className={styles.pullQuote}>
              <p>Preserving the past keeps us connected today. That’s why we created MyCanvas.</p>
            </blockquote>
            {/* After the quote in the DOM so phones show the photo mid-story, desktop moves it to the right column */}
            <figure className={styles.storyFigure}>
              <div className={styles.storyPhoto}>
                <CloudinaryPicture
                  prefix={IMAGE_PREFIX}
                  imageKey="softcover-books_mycanvas/5"
                  widths={IMAGE_WIDTHS}
                  sizes="(min-width: 992px) 500px, calc(100vw - 32px)"
                  className={styles.coverImage}
                  loading="lazy"
                />
              </div>
              <figcaption className={styles.caption}>
                Softcover Photo Books, including Grandpa’s Story.
              </figcaption>
            </figure>
            <p className={styles.storyParagraph}>
              We specialize in printing your family memories as wall art and keepsake books. Whether
              you’re printing your genealogical history into a leather-bound photo book or
              showcasing your family tree on a poster, we make it easy to begin sharing your story
              in tangible and meaningful ways.
            </p>
            <p className={styles.storyParagraph}>
              We also know there’s more history to be made. Our photo prints expand beyond family
              history mementos. We love to print the new memories that your families are making
              every day.
            </p>
          </div>
        </section>

        <section className={styles.usa} aria-labelledby="about-usa">
          <div className={styles.container}>
            <div className={styles.usaHead}>
              <div className={styles.titleColumn}>
                <p className={`${styles.eyebrow} ${styles.eyebrowInverse}`}>
                  <Stars size={14} />
                  Made in the USA
                </p>
                <h2 id="about-usa" className={styles.usaTitle}>
                  Made right here in America
                </h2>
              </div>
              <p className={styles.usaBody}>
                Every product we create is made in the USA by our teams in South Carolina,
                Pennsylvania and Utah. Our family history photo prints, calendars and books are made
                with the highest-quality materials and longevity in mind.
              </p>
            </div>

            <ul className={styles.states}>
              {STATES.map(({ name, code }) => (
                <li key={code} className={styles.state}>
                  <span className={styles.stateName}>{name}</span>
                  <span className={styles.stateCode} aria-hidden="true">
                    {code}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className={styles.products} aria-labelledby="about-products">
          <div className={styles.container}>
            <div className={styles.productsHead}>
              <div className={styles.titleColumn}>
                <p className={styles.eyebrow}>Our products</p>
                <h2 id="about-products" className={styles.sectionTitle}>
                  Keepsakes for every family story
                </h2>
              </div>
              <p className={styles.productsIntro}>
                Connect your family tree on Ancestry.com or FamilySearch.org and turn your research
                into ready-to-print books, posters and calendars.
              </p>
            </div>

            <ul className={styles.tiles}>
              {PRODUCT_TILES.map(({ id, imageKey, title, description, linkLabel, url }) => (
                <li key={id} className={styles.tile}>
                  <div className={styles.tileImage}>
                    <CloudinaryPicture
                      prefix={IMAGE_PREFIX}
                      imageKey={imageKey}
                      widths={IMAGE_WIDTHS}
                      sizes="(min-width: 992px) 404px, calc(100vw - 32px)"
                      className={styles.coverImage}
                      loading="lazy"
                    />
                  </div>
                  <div className={styles.tileBody}>
                    <h3 className={styles.tileTitle}>{title}</h3>
                    <p className={styles.tileDescription}>{description}</p>
                    <Link
                      href={url}
                      className={styles.tileLink}
                      onClick={() => trackCtaClick('product', 'products', url)}
                    >
                      {linkLabel}
                      <ArrowRight size={20} aria-hidden="true" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className={styles.closing} aria-labelledby="about-closing">
          <div className={`${styles.container} ${styles.closingRow}`}>
            <div className={styles.closingText}>
              <h2 id="about-closing" className={styles.sectionTitle}>
                Begin sharing your story
              </h2>
              <p className={styles.closingBody}>
                Bring your family history into the present, printed with care in the USA.
              </p>
            </div>
            <Link
              href={CLOSING_URL}
              className={styles.primaryButton}
              onClick={() => trackCtaClick('explore', 'closing', CLOSING_URL)}
            >
              Explore family history
              <ArrowRight size={20} aria-hidden="true" />
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  </>
);

export default AboutPage;
